/**
 * BuilderShell — shared layout wrapper for all section builders.
 *
 * Encapsulates the common structure:
 *   - TopBar with Reset / Share / Save / Run actions
 *   - Collapsible configuration card with AnimatePresence
 *   - Content area for results
 *   - SaveReportModal integration
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play,
  Save,
  Link2,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { TopBar } from '@/components/layout/TopBar';
import { SaveReportModal } from '@/components/reports/SaveReportModal';
import { fadeInUp } from '@/design/animations';
import { useShareUrl } from '@/hooks/useShareUrl';
import { setLastSectionUrl, clearLastSectionUrl } from '@/lib/sectionLastUrl';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { toast } from '@/components/ui/Toast';
import { useDataFreshness, resetDataFreshness } from '@/services/dataFreshness';

/** Format a YYYY-MM-DD watermark as a short, readable date (e.g. "Jul 3, 2026"). */
function formatWatermark(watermark: string): string {
  const parsed = new Date(`${watermark}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return watermark;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

interface SkippedConfig {
  displayName: string;
  reason: 'fresh' | 'already_running';
}

interface RefreshTriggerResult {
  jobId: string;
  triggered: number;
  skipped: SkippedConfig[];
  done: boolean;
  matched: number;
}

interface RefreshStatusResult {
  jobId: string;
  state: 'running' | 'done';
  total: number;
  remaining: number;
  triggered: number;
  skipped: SkippedConfig[];
  failed: string[];
}

interface BuilderShellProps {
  /** Title for the TopBar. */
  title: string;
  /** Subtitle for the TopBar. */
  subtitle: string;
  /** Whether to show the back button. */
  showBack?: boolean;
  /** Route to navigate back to. */
  backTo?: string;
  /** Called when the Reset button is clicked. */
  onReset: () => void;
  /** Called when the Run button is clicked. */
  onRun: () => void;
  /** Whether a query is currently running. */
  isRunning?: boolean;
  /** Whether the Run button should be disabled. */
  canRun?: boolean;

  // ─── Save modal ─────────────────────────────────────────────────────
  /** Section ID for saving reports. */
  sectionId: string;
  /** Section route for navigation after save (e.g., '/product'). */
  sectionRoute: string;
  /** Existing report ID (when editing a saved report). */
  reportId?: string;
  /** Saved report data (for pre-filling name/description). */
  savedReport?: { report?: { name?: string; description?: string } } | null;
  /** Current config to save. */
  configForSave: Record<string, unknown>;

  // ─── Slots ──────────────────────────────────────────────────────────
  /** Config panel content (rendered inside the collapsible card). */
  configPanel: React.ReactNode;
  /** Results panel content (rendered below the config card). */
  children: React.ReactNode;
  /** Additional action buttons to add before the standard ones. */
  extraActions?: React.ReactNode;
}

export function BuilderShell({
  title,
  subtitle,
  showBack = true,
  backTo,
  onReset,
  onRun,
  isRunning = false,
  canRun = true,
  sectionId,
  sectionRoute,
  reportId,
  savedReport,
  configForSave,
  configPanel,
  children,
  extraActions,
}: BuilderShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [configExpanded, setConfigExpanded] = useState(true);
  const [configAnimDone, setConfigAnimDone] = useState(true);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const handleCopyUrl = useShareUrl();

  // ─── Persist current URL so the section can be restored on re-entry ───
  useEffect(() => {
    const fullUrl = location.pathname + (searchParams.toString() ? `?${searchParams.toString()}` : '');
    setLastSectionUrl(sectionRoute, fullUrl);
  }, [sectionRoute, location.pathname, searchParams]);

  // ─── Back button: clear saved URL so user lands on reports listing ────
  const handleBack = useCallback(() => {
    clearLastSectionUrl(sectionRoute);
    navigate(backTo ?? sectionRoute);
  }, [navigate, backTo, sectionRoute]);

  const handleSaved = useCallback(
    (report: { id: string }) => {
      setShowSaveModal(false);
      queryClient.invalidateQueries({ queryKey: ['reports', sectionId] });
      navigate(`${sectionRoute}/reports/${report.id}${window.location.search}`);
    },
    [navigate, queryClient, sectionId, sectionRoute],
  );

  const [clearingCache, setClearingCache] = useState(false);
  const handleClearCache = useCallback(async () => {
    setClearingCache(true);
    try {
      await api.post(endpoints.cache.clear());
      toast.success('Cache cleared');
    } catch {
      toast.error('Failed to clear cache');
    } finally {
      setClearingCache(false);
    }
  }, []);

  // ─── Data freshness gate indicator (published by the API client) ──────────
  const freshness = useDataFreshness();
  useEffect(() => { resetDataFreshness(); }, [sectionId]);

  // ─── Refresh Data: force-run the BigQuery scheduled queries, poll to done ──
  const [refreshingData, setRefreshingData] = useState(false);
  const refreshCancelled = useRef(false);
  useEffect(() => () => { refreshCancelled.current = true; }, []);

  const handleRefreshData = useCallback(async () => {
    setRefreshingData(true);
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    try {
      const res = await api.post<RefreshTriggerResult>(endpoints.data.refresh(), { section: sectionId });

      if (res.done) {
        toast.success(res.skipped.length ? 'Data already up to date' : 'Data refreshed');
        return;
      }

      toast.info(`Refreshing data (${res.triggered} ${res.triggered === 1 ? 'query' : 'queries'})...`);

      const MAX_ATTEMPTS = 150; // ~10 min at 4s intervals
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        await sleep(4000);
        if (refreshCancelled.current) return;
        const status = await api.get<RefreshStatusResult>(endpoints.data.refreshStatus(res.jobId));
        if (status.state === 'done') {
          if (status.failed.length > 0) {
            toast.error(`Data refresh finished with ${status.failed.length} failed run(s)`);
          } else {
            toast.success('Data refreshed');
          }
          return;
        }
      }
      toast.error('Data refresh is taking longer than expected; check back shortly');
    } catch {
      toast.error('Failed to refresh data');
    } finally {
      if (!refreshCancelled.current) setRefreshingData(false);
    }
  }, [sectionId]);

  const displayTitle = reportId
    ? savedReport?.report?.name || 'Report'
    : title;

  return (
    <>
      <TopBar
        title={displayTitle}
        subtitle={subtitle}
        showBack={showBack}
        onBack={handleBack}
        actions={
          <div className="flex items-center gap-2">
            {freshness?.gated && freshness.watermark && (
              <span
                className="text-xs text-amber-600 whitespace-nowrap"
                title="The latest requested day isn't fully loaded yet, so results are capped to this date. Use Refresh Data to run the pending queries."
              >
                Data as of {formatWatermark(freshness.watermark)}
              </span>
            )}
            {extraActions}
            <Button
              variant="ghost"
              size="sm"
              icon={<RefreshCw />}
              onClick={handleRefreshData}
              loading={refreshingData}
              title="Force-run the BigQuery scheduled queries that build the data tables"
            >
              Refresh Data
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={<Trash2 />}
              onClick={handleClearCache}
              loading={clearingCache}
            >
              Clear Cache
            </Button>
            <Button variant="ghost" size="sm" icon={<RotateCcw />} onClick={onReset}>
              Reset
            </Button>
            <Button variant="ghost" size="sm" icon={<Link2 />} onClick={handleCopyUrl}>
              Share
            </Button>
            <Button
              variant="outline"
              size="sm"
              icon={<Save />}
              onClick={() => setShowSaveModal(true)}
            >
              Save
            </Button>
            <Button
              size="sm"
              icon={<Play />}
              onClick={onRun}
              loading={isRunning}
              disabled={!canRun}
            >
              Run
            </Button>
          </div>
        }
      />

      <div className="px-8 py-6 space-y-4">
        {/* Configuration Panel */}
        <motion.div variants={fadeInUp} initial="hidden" animate="visible">
          <Card padding="none">
            <button
              onClick={() => setConfigExpanded(!configExpanded)}
              className={cn(
                'w-full flex items-center justify-between px-5 py-3',
                'text-sm font-medium text-slate-700',
                'hover:bg-slate-50/50 transition-colors duration-150',
                configExpanded && 'border-b border-slate-100',
              )}
            >
              <span>Configuration</span>
              {configExpanded ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </button>

            <AnimatePresence initial={false}>
              {configExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                  onAnimationStart={() => setConfigAnimDone(false)}
                  onAnimationComplete={(def) => {
                    if (typeof def === 'object' && 'height' in def && def.height === 'auto') {
                      setConfigAnimDone(true);
                    }
                  }}
                  className={configAnimDone ? 'overflow-visible' : 'overflow-hidden'}
                >
                  {configPanel}
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </motion.div>

        {/* Results */}
        {children}
      </div>

      {/* Save Report Modal */}
      {showSaveModal && (
        <SaveReportModal
          config={configForSave}
          sectionId={sectionId}
          existingReportId={reportId}
          existingName={reportId ? savedReport?.report?.name : undefined}
          existingDescription={reportId ? savedReport?.report?.description : undefined}
          onClose={() => setShowSaveModal(false)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
